/**
 * Fire-and-forget audio playback for one-shot sounds (moos, brays, etc.).
 *
 * IMPORTANT: We keep a persistent registry of currently-playing Audio
 * elements on `window` so that the Audio object is NOT garbage-collected
 * (and its `load()`/`play()` promise is NOT aborted) when the calling
 * component unmounts — for example, when a chapter's <NextPage> `<Link>`
 * triggers a React Router navigation immediately after firing the sound.
 *
 * Elements remove themselves from the registry once playback ends or errors.
 *
 * Options:
 *   volume:    0.0 – 1.0, default 0.38 (~38%)
 *   singleton: when true, stops any currently-playing pooled audio before
 *              starting this one. Used by the "More Than a Herd" preview
 *              buttons so only one animal sound plays at a time.
 */
export function playSound(url, volume = 0.38, options = {}) {
  if (typeof window === "undefined" || !url) return;
  try {
    if (!window.__saltyAudioPool) {
      window.__saltyAudioPool = new Set();
    }
    if (options.singleton && window.__saltyAudioPool.size) {
      // Stop everything already playing so this new sound is heard alone.
      for (const a of Array.from(window.__saltyAudioPool)) {
        try { a.pause(); a.currentTime = 0; } catch { /* noop */ }
        window.__saltyAudioPool.delete(a);
      }
    }
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.volume = Math.max(0, Math.min(1, volume));

    // Pin the reference so it survives the calling component's unmount.
    window.__saltyAudioPool.add(audio);
    const release = () => {
      try {
        window.__saltyAudioPool.delete(audio);
      } catch {
        /* noop */
      }
    };
    audio.addEventListener("ended", release, { once: true });
    audio.addEventListener("error", release, { once: true });

    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Autoplay/permission failure — silently ignore. Since this is
        // called from a click handler it should almost never fire.
        release();
      });
    }
  } catch {
    // Older browsers or unsupported environments — silent.
  }
}
